import { c as _c } from "react/compiler-runtime";
import { Accordion, AccordionDetails, AccordionSummary, Box, ClickAwayListener, Divider, FormControlLabel, IconButton, Paper, Popper, Stack, Switch, Tooltip, Typography } from '@mui/material';
import type { Condition, Control } from '@repo/api-gw-sdk';
import { BackupStatus } from '@repo/api-gw-sdk';
import classnames from 'classnames';
import { useState } from 'react';
import { HelpIcon } from '@/components/shared/helpIcon';
import { useDAL } from '@/contexts/useDAL';
import { BackupStatuses, ControlsSeverity } from '@/data/inventory/data';
import { isValueCondition } from '@/types/advanceFilter';
import { BackupStatusIcon } from './backupStatusIcon';
import { createBackupStatusNotInCondition } from './createBackupStatusNotInCondition';
import { ControlIndicator, controlKeysPriorities } from '../controls/controlIndicator';
import { ControlsListFilter } from '../controls/controlsListFilter';
import { CONTROL_ID_PROPERTY, MUTED_CONTROL_ID_PROPERTY } from '../queryBuilder/propertyNames';
import { Icon } from '../shared/icon';
interface FilterProps {
  backupStatus: BackupStatus;
  toggleBackupStatus: (status: BackupStatus) => void;
  selectedBackupStatuses: string[];
}
export const ViolationsDetectedFilterRow = (props: FilterProps & {
  selectedLevel: string | undefined;
  renderDrillDownFilter: (title: string, subtitle: string, component: (props: {
    filter: Condition;
  }) => React.ReactElement) => void;
  onFilterChange: (condition: Condition[]) => void;
  conditions: Condition[];
  selectControlLevel: (controlLevel: string) => void;
}) => {
  const dal = useDAL();
  const {
    data: body
  } = dal.controls.useList();
  const condition = props.conditions.find(x => isValueCondition(x) && x.property === CONTROL_ID_PROPERTY);
  const mutedCondition = props.conditions.find(x_0 => isValueCondition(x_0) && x_0.property === MUTED_CONTROL_ID_PROPERTY);
  const selectedResources = isValueCondition(condition) && condition?.value || isValueCondition(mutedCondition) && mutedCondition?.value || [];
  const severityWithFilteredControls = mutedCondition ? 'muted' : findFirstControl(selectedResources, body?.controls)?.severity.toLowerCase();
  const selected = props.selectedBackupStatuses.includes(props.backupStatus);
  return <Accordion className='bg-transparent m-0 mr-[-24px]' expanded={selected || !!props.selectedLevel}>
      <AccordionSummary className='p-0 bg-transparent' sx={{
      '.MuiStack-root': {
        flexGrow: 1,
        width: '100%'
      },
      '.MuiTypography-root': {
        /* eslint-disable-next-line @eon/no-manual-typography */
        fontWeight: 'normal'
      }
    }} expandIcon={null}>
        <BackupStatusFilterRow {...props} />
      </AccordionSummary>
      <AccordionDetails className='p-0'>
        <Stack className='my-[12px]'>
          {controlKeysPriorities.map(priority => {
          const selected_0 = props.selectedLevel === priority;
          return <Stack key={priority} data-testid={`filter-severity-${priority}`} sx={theme => ({
            margin: '0 0 0 -24px',
            paddingLeft: '32px',
            borderLeft: '2px solid transparent',
            borderColor: selected_0 ? 'var(--mui-palette-custom-BLACK-01)' : 'transparent',
            backgroundColor: selected_0 ? 'var(--mui-palette-background-default)' : 'transparent',
            cursor: selected_0 ? 'default' : 'pointer',
            ...(!selected_0 ? {
              '&:hover': {
                backgroundColor: 'white'
              },
              ...theme.applyStyles('dark', {
                '&:hover': {
                  backgroundColor: 'var(--mui-palette-custom-BLACK-01)'
                }
              })
            } : {})
          })} onClick={() => props.selectControlLevel(priority)}>
                <Stack direction={'row'} sx={{
              alignItems: 'center',
              borderLeft: '1px solid var(--mui-palette-divider)',
              padding: '4px 0 4px 20px'
            }}>
                  <ControlIndicator backgroundColor={ControlsSeverity[priority].color} />
                  <Typography className='ml-[12px] grow'>
                    {ControlsSeverity[priority].title}
                  </Typography>
                  {severityWithFilteredControls === priority && <Box className='rounded inline-block mr-[10px] h-[8px] w-[8px]' sx={{
                backgroundColor: 'currentColor'
              }} />}
                  <Tooltip title={priority === 'muted' ? 'Filter by muted violated controls' : 'Filter by violated controls'}>
                    <IconButton data-testid={`filter-severity-${priority}-drill-down`} className={classnames('mr-[18px]', {
                  collapse: !(selected_0 || severityWithFilteredControls === priority)
                })} onClick={event => {
                  props.renderDrillDownFilter('Violated Controls', `Select controls from this list to filter for resources with  ${priority === 'muted' ? priority : `${priority}-severity`} violations`, ({
                    filter
                  }) => <ControlsListFilter conditions={filter.conditions || []} onChange={props.onFilterChange} priority={priority} />);
                  event.stopPropagation();
                }}>
                      <i className='material-symbols-arrow-right-rounded' />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>;
        })}
        </Stack>
      </AccordionDetails>
    </Accordion>;
};
export const BackupStatusFilterRow = t0 => {
  const $ = _c(30);
  const {
    backupStatus,
    toggleBackupStatus,
    selectedBackupStatuses
  } = t0;
  let t1;
  if ($[0] !== backupStatus || $[1] !== selectedBackupStatuses) {
    t1 = selectedBackupStatuses.includes(backupStatus);
    $[0] = backupStatus;
    $[1] = selectedBackupStatuses;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected = t1;
  let t2;
  if ($[3] !== backupStatus || $[4] !== toggleBackupStatus) {
    t2 = () => toggleBackupStatus(backupStatus);
    $[3] = backupStatus;
    $[4] = toggleBackupStatus;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      alignItems: "center"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== selected) {
    t4 = [t3, theme => ({
      margin: "0 -24px",
      padding: "12px 0 12px 22px",
      borderLeft: "2px solid transparent",
      borderColor: selected ? "var(--mui-palette-custom-BLACK-01)" : "transparent",
      backgroundColor: selected ? "var(--mui-palette-background-default)" : "transparent",
      cursor: "pointer",
      ...(!selected ? {
        "&:hover": {
          backgroundColor: "white"
        },
        ...theme.applyStyles("dark", {
          "&:hover": {
            backgroundColor: "var(--mui-palette-custom-BLACK-01)"
          }
        })
      } : {})
    })];
    $[7] = selected;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== backupStatus) {
    t5 = <BackupStatusIcon backupStatus={backupStatus} />;
    $[9] = backupStatus;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = BackupStatuses[backupStatus];
  let t7;
  if ($[11] !== t6.title) {
    t7 = <Typography className="ml-[8px]">{t6.title}</Typography>;
    $[11] = t6.title;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = BackupStatuses[backupStatus];
  let t9;
  if ($[13] !== backupStatus) {
    t9 = BackupStatuses[backupStatus].recommendation && <Divider className="my-[8px]" sx={{
      borderColor: "var(--mui-palette-custom-PURPLE-02)"
    }} />;
    $[13] = backupStatus;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== backupStatus) {
    t10 = BackupStatuses[backupStatus].recommendation && <Stack direction="row"><Icon sx={_temp} iconClass="material-symbols-circle-notifications-outline-rounded" />{BackupStatuses[backupStatus].recommendation}</Stack>;
    $[15] = backupStatus;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8.description || $[19] !== t9) {
    t11 = <Box>{t8.description}{t9}{t10}</Box>;
    $[17] = t10;
    $[18] = t8.description;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      slotProps: {
        tooltip: {
          sx: {
            padding: "16px"
          }
        }
      }
    };
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11) {
    t13 = <HelpIcon className="absolute right-[24px] cursor-default" title={t11} tooltipProps={t12} />;
    $[22] = t11;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t2 || $[26] !== t4 || $[27] !== t5 || $[28] !== t7) {
    t14 = <Stack className="group/help" onClick={t2} direction="row" sx={t4}>{t5}{t7}{t13}</Stack>;
    $[24] = t13;
    $[25] = t2;
    $[26] = t4;
    $[27] = t5;
    $[28] = t7;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
};
function findFirstControl(selectedResources: string[], controls: Control[] | undefined) {
  for (const id of selectedResources) {
    const control = controls?.find(x => x.id === id);
    if (control) {
      return control;
    }
  }
}
export const AdditionalStatuses = t0 => {
  const $ = _c(20);
  const {
    valueConditions,
    onChange,
    excludedBackupStatusCondition,
    includedBackupStatusCondition
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      justifyContent: "space-between"
    };
    t2 = <Typography>Additional statuses</Typography>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconButton ref={setAnchorEl} onClick={() => setOpen(true)}><i className="material-symbols-more-horiz" /></IconButton>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      zIndex: 1,
      marginTop: "-44px !important",
      width: "308px"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      borderRadius: "8px",
      boxShadow: "var(--mui-customShadows-lg)"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setOpen(false);
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      gap: "12px"
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      alignItems: "center",
      justifyContent: "space-between"
    };
    t9 = <Typography className="font-semibold">Additional statuses</Typography>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  let t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Stack direction="row" sx={t8}>{t9}<Tooltip title="Close"><IconButton onClick={() => setOpen(false)}><i className="material-symbols-close-rounded" /></IconButton></Tooltip></Stack>;
    t11 = <Typography className="font-light mb-[8px]">Resources with these statuses are hidden from your inventory by default. To work with the hidden resources, turn on the statuses below.</Typography>;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  let t12;
  if ($[11] !== excludedBackupStatusCondition || $[12] !== includedBackupStatusCondition || $[13] !== onChange || $[14] !== valueConditions) {
    t12 = <Paper className="px-[24px] py-[12px]" sx={t5}><ClickAwayListener onClickAway={t6}><Stack className="pb-[8px]" sx={t7}>{t10}{t11}<HiddenStatusSwitch valueConditions={valueConditions} excludedBackupStatusCondition={excludedBackupStatusCondition} includedBackupStatusCondition={includedBackupStatusCondition} onChange={onChange} backupStatus={BackupStatus.Disconnected} /><HiddenStatusSwitch valueConditions={valueConditions} excludedBackupStatusCondition={excludedBackupStatusCondition} includedBackupStatusCondition={includedBackupStatusCondition} onChange={onChange} backupStatus={BackupStatus.ExcludedFromBackup} /><HiddenStatusSwitch valueConditions={valueConditions} excludedBackupStatusCondition={excludedBackupStatusCondition} includedBackupStatusCondition={includedBackupStatusCondition} onChange={onChange} backupStatus={BackupStatus.Unsupported} /><HiddenStatusSwitch valueConditions={valueConditions} excludedBackupStatusCondition={excludedBackupStatusCondition} includedBackupStatusCondition={includedBackupStatusCondition} onChange={onChange} backupStatus={BackupStatus.LimitExceeded} /></Stack></ClickAwayListener></Paper>;
    $[11] = excludedBackupStatusCondition;
    $[12] = includedBackupStatusCondition;
    $[13] = onChange;
    $[14] = valueConditions;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== anchorEl || $[17] !== open || $[18] !== t12) {
    t13 = <Stack direction="row" sx={t1}>{t2}{t3}<Popper open={open} anchorEl={anchorEl} disablePortal={true} placement="bottom" sx={t4}>{t12}</Popper></Stack>;
    $[16] = anchorEl;
    $[17] = open;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  return t13;
};
const HiddenStatusSwitch = t0 => {
  const $ = _c(23);
  const {
    backupStatus,
    valueConditions,
    onChange,
    excludedBackupStatusCondition,
    includedBackupStatusCondition
  } = t0;
  let t1;
  if ($[0] !== backupStatus || $[1] !== excludedBackupStatusCondition?.value) {
    t1 = excludedBackupStatusCondition?.value?.includes(backupStatus);
    $[0] = backupStatus;
    $[1] = excludedBackupStatusCondition?.value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isBackupStatusChecked = !t1;
  let t2;
  if ($[3] !== backupStatus || $[4] !== excludedBackupStatusCondition || $[5] !== includedBackupStatusCondition || $[6] !== isBackupStatusChecked || $[7] !== onChange || $[8] !== valueConditions) {
    t2 = () => {
      if (isBackupStatusChecked) {
        const excludedStatuses = [...(excludedBackupStatusCondition?.value || []), backupStatus];
        const includedStatuses = includedBackupStatusCondition?.value?.filter(x => x !== backupStatus);
        onChange(valueConditions.filter(x_0 => x_0 !== includedBackupStatusCondition && x_0 !== excludedBackupStatusCondition).concat(createBackupStatusNotInCondition(excludedStatuses), includedStatuses?.length ? [{
          ...includedBackupStatusCondition,
          value: includedStatuses
        }] : []));
      } else {
        const excludedStatuses_0 = excludedBackupStatusCondition?.value?.filter(x_1 => x_1 !== backupStatus);
        onChange(valueConditions.filter(x_2 => x_2 !== excludedBackupStatusCondition).concat(excludedStatuses_0?.length ? [createBackupStatusNotInCondition(excludedStatuses_0)] : []));
      }
    };
    $[3] = backupStatus;
    $[4] = excludedBackupStatusCondition;
    $[5] = includedBackupStatusCondition;
    $[6] = isBackupStatusChecked;
    $[7] = onChange;
    $[8] = valueConditions;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Switch />;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      alignItems: "center",
      gap: "12px"
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== backupStatus) {
    t5 = <BackupStatusIcon backupStatus={backupStatus} />;
    $[12] = backupStatus;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = BackupStatuses[backupStatus];
  let t7;
  if ($[14] !== t6.title) {
    t7 = <Typography>{t6.title}</Typography>;
    $[14] = t6.title;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <Stack direction="row" sx={t4}>{t5}{t7}</Stack>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isBackupStatusChecked || $[20] !== t2 || $[21] !== t8) {
    t9 = <FormControlLabel className="p-0 m-0 w-full justify-between" labelPlacement="start" checked={isBackupStatusChecked} onClick={t2} control={t3} label={t8} />;
    $[19] = isBackupStatusChecked;
    $[20] = t2;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
function _temp(theme_0) {
  return {
    marginRight: "8px",
    "& i": {
      color: "var(--mui-palette-common-white)",
      ...theme_0.applyStyles("dark", {
        color: "var(--mui-palette-custom-PURPLE-02)"
      })
    }
  };
}